import React, {Suspense} from "react";
import Loading from "../components/common/Loading";
import {Icons} from "../constants/svgIcons";
import {useTranslation} from "react-i18next";
import {homePageData, serviceCards, whyChoose} from "../constants/data";
import {Link} from "react-router-dom";
import HomeInclude from "../components/sections/HomeInclude";
import CustomSoftwareStudio from "../components/sections/CustomSoftwareStudio";

const Home = () => {
  const {t} = useTranslation();
  // Pass the namespace/prefix directly
  const lang = (key) => t(`homePage.${key}`);

  const stats = [
    { num: '200+', label: lang('hero.stats.projects') },
    { num: '80+', label: lang('hero.stats.clients') },
    { num: '15+', label: lang('hero.stats.countries') },
    { num: '7+', label: lang('hero.stats.experience') },
  ];

  return (
    <div>
      <Suspense fallback={<Loading />}>
        <div
          // dir={isRTL}
        >
          {/* Hero */}
          <section className="relative min-h-screen bg-theme-color flex items-center overflow-hidden">
            <div className="absolute inset-0 grid-pattern"></div>
            {/* Animated background */}
            <div className="absolute inset-0 overflow-hidden">
              <div className="absolute top-20 right-20 w-96 h-96 bg-white/5 rounded-full blur-3xl animate-pulse"/>
              <div className="absolute bottom-20 left-20 w-80 h-80 bg-white/5 rounded-full blur-3xl animate-pulse"
                   style={{animationDelay: '1s'}}/>
              <div
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/3 rounded-full blur-3xl"/>
              {/* Grid pattern */}
              <div className="absolute inset-0 opacity-10"
                   style={{
                     backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px,transparent 1px),linear-gradient(90deg,rgba(255,255,255,0.1) 1px,transparent 1px)',
                     backgroundSize: '60px 60px'
                   }}/>
            </div>

            <div className="md:w-theme-container w-mobile-container mx-auto relative  pt-32 pb-20">
              <div className="grid lg:grid-cols-2 gap-12 items-center">
                <div>
                  <div
                    className="inline-flex items-center gap-2 bg-white/15 border border-white/30 rounded-full px-5 py-2 mb-8">
                    <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"/>
                    <span className="text-white/90 text-sm font-medium">{lang('hero.badge')}</span>
                  </div>
                  <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6">
                    {lang('hero.title')}<br/>
                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-300 to-orange-300">
                  {lang('hero.titleHighlight')}
                </span>
                  </h1>
                  <p className="text-white/80 text-lg leading-relaxed mb-10 max-w-xl">
                    {lang('hero.subtitle')}
                  </p>
                  <div className="flex flex-wrap gap-4">
                    <a href="#services-section"
                       onClick={e => {
                         e.preventDefault();
                         document.getElementById('services-section')?.scrollIntoView({behavior: 'smooth'});
                       }}
                       className="bg-white text-theme-color px-8 py-4 rounded-full font-bold text-base hover:shadow-2xl hover:scale-105 transition-all inline-flex items-center gap-2">
                      {lang('hero.cta')} <Icons.Arrow/>
                    </a>
                    <Link to={'/contact'}
                       className="border-2 border-white/50 text-white px-8 py-4 rounded-full font-bold text-base hover:bg-white/10 transition-all">
                      {lang('hero.ctaSecondary')}
                    </Link>
                  </div>
                </div>
                {/* Hero Visual */}
                <div className="hidden lg:block relative">
                  <div className="relative w-full aspect-square max-w-lg mx-auto">
                    <div
                      className="absolute inset-0 rounded-3xl overflow-hidden border border-white/20 shadow-2xl">
                      <img src="https://images.unsplash.com/photo-1519389950473-47ba0277781c?w=800&q=80" alt="Tech team"
                           className="w-full h-full object-cover opacity-70 mix-blend-luminosity"/>
                      <div
                        className="absolute inset-0 bg-gradient-to-t from-theme-color via-transparent to-transparent"/>
                    </div>
                    {/* Floating cards */}
                    <div
                      className="absolute -left-12 top-1/4 bg-white rounded-2xl shadow-2xl p-4 flex items-center gap-3 animate-bounce"
                      style={{animationDuration: '3s'}}>
                      <div
                        className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center text-green-600">
                        <Icons.Check/></div>
                      <div>
                        <div className="text-xs text-gray-500 font-medium">{t('common.projectDelivered')}</div>
                        <div className="text-sm font-bold text-gray-800">Logistics Software </div>
                      </div>
                    </div>
                    <div className="absolute -right-12 bottom-1/1 bg-white rounded-2xl shadow-2xl p-4 animate-bounce"
                         style={{animationDuration: '4s', animationDelay: '1s'}}>
                      <div className="text-xs text-gray-500 mb-1 font-medium">{t('common.clientSatisfaction')}</div>
                      <div className="flex items-center gap-1">
                        {[1, 2, 3, 4, 5].map(i => <span key={i} className="text-yellow-400 text-sm">★</span>)}
                        <span className="text-sm font-bold text-gray-800 ml-1">5.0</span>
                      </div>
                    </div>
                    <div
                      className="absolute -right-8 bottom-1/4 bg-white rounded-2xl shadow-2xl p-4 text-white animate-bounce"
                      style={{animationDuration: '4s', animationDelay: "0.8s"}}
                    >
                      <div className="text-[11px] text-slate-400 mb-1">{t('common.countriesServed')}</div>
                      <div className="text-2xl text-black">
                        3+ 🌍
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats */}
              <div className="hidden grid grid-cols-2 md:grid-cols-4 gap-6 mt-16 pt-16 border-t border-white/20">
                {stats.map((s, i) => (
                  <div key={i} className="text-center">
                    <div className="text-4xl font-black text-white mb-1">{s.num}</div>
                    <div className="text-white/70 text-sm font-medium">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Services */}
          <section id="services-section" className="py-24 bg-gray-50">
            <div className="md:w-theme-container w-mobile-container mx-auto">
              <div className="text-center mb-16">
                <div
                  className="inline-block bg-theme-color/10 text-theme-color capitalize text-sm font-semibold px-4 py-2 rounded-full mb-4">
                  What we do
                </div>
                <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-3">{lang('services.title')}</h2>
                <h2 className="text-lg md:text-xl text-gray-500 mb-4">{lang('services.subtitle')}</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {serviceCards.map(({ key, icon, href, img, color, title, short, desc, url }) => (
                <a
                  key={key}
                  href={url}
                  className="group relative bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 border border-gray-100">
                  <div className="relative h-48 overflow-hidden">
                    <img alt={`service-photo-${key}`} src={img} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700" />
                    <div className={`absolute inset-0 bg-gradient-to-t ${color} opacity-60 group-hover:opacity-70 transition-opacity`} />
                    <div className="absolute top-4 left-4 bg-white/20 backdrop-blur-sm text-white p-3 rounded-2xl border border-white/30">
                      {icon}
                    </div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-theme-color transition-colors">{lang(`services.${title}`)}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4">{lang(`services.${desc}`)}</p>
                    <div className="flex items-center gap-2 text-theme-color text-sm font-semibold">
                      {/*<span>{lang === 'ar' ? 'اعرف المزيد' : 'Learn More'}</span>*/}
                      <span>Learn More</span>
                      <Icons.Arrow />
                    </div>
                  </div>
                </a>
              ))}
              </div>
            </div>
          </section>

          <div className="md:w-theme-container w-mobile-container mx-auto">
            <HomeInclude />
          </div>
          <CustomSoftwareStudio/>
          {/* Why Choose Us */}
          <section className="py-24 bg-theme-color">
            <div className="md:w-theme-container w-mobile-container mx-auto">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-black text-white mb-4">{lang('why.title')}</h2>
                <p className="text-white/70 text-lg max-w-2xl mx-auto">{lang('why.subtitle')}</p>
              </div>
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                {whyChoose.map((item, i) => (
                  <div key={i} className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-3xl p-6 hover:bg-white/20 transition-all group">
                    <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center text-white text-xl font-black mb-4 group-hover:scale-110 transition-transform">
                      {['🌍', '⚡', '🛡️', '🏆'][i]}
                    </div>
                    <h3 className="text-white font-bold text-lg mb-2">{lang(`why.${item.title}`)}</h3>
                    <p className="text-white/70 text-sm leading-relaxed">{lang(`why.${item.desc}`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* Process */}
          <section className="py-24 bg-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center mb-16">
                <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4">{lang('process.title')}</h2>
              </div>
              <div className="grid md:grid-cols-4 gap-8 relative">
                <div className="hidden md:block absolute top-10 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-theme-color/30 to-transparent mx-32" />
                {homePageData.process.steps.map((step, i) => (
                  <div key={i} className="relative text-center group">
                    <div className="w-20 h-20 bg-theme-color text-white rounded-3xl flex items-center justify-center text-2xl font-black mx-auto mb-6 group-hover:scale-110 transition-transform shadow-lg shadow-theme-color/30">
                      {step.num}
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{lang(`process.${step.translateKey}`)}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{lang(`process.${step.translateDesc}`)}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

           {/*Testimonials strip */}
          {/*<section className="py-16 bg-gray-50 overflow-hidden">*/}
          {/*  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">*/}
          {/*    <div className="grid md:grid-cols-3 gap-6">*/}
          {/*      {testimonialList.map((t, i) => (*/}
          {/*        <div key={i} className="bg-white rounded-3xl p-6 shadow-sm border border-gray-100 hover:shadow-lg transition-shadow">*/}
          {/*          <div className="flex gap-1 text-yellow-400 mb-4">{[1,2,3,4,5].map(s => <Icons.Star key={s} />)}</div>*/}
          {/*          /!*<p className="text-gray-700 text-sm leading-relaxed mb-6 italic">{t?.en.text}</p>*!/*/}
          {/*          <div className="flex items-center gap-3">*/}
          {/*            <div className="w-10 h-10 bg-theme-color rounded-full flex items-center justify-center text-white font-bold text-sm">*/}
          {/*              {t.en.name[0]}*/}
          {/*            </div>*/}
          {/*            <div>*/}
          {/*              <div className="font-bold text-gray-900 text-sm">{t.en.name}</div>*/}
          {/*              <div className="text-gray-500 text-xs">{t.en.role}</div>*/}
          {/*            </div>*/}
          {/*          </div>*/}
          {/*        </div>*/}
          {/*      ))}*/}
          {/*    </div>*/}
          {/*  </div>*/}
          {/*</section>*/}

          {/* CTA Banner */}
          <section className="py-24 bg-gradient-to-r from-zinc-600  via-theme-color to-gray-800 relative overflow-hidden">
            <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 30% 50%, white 1px, transparent 1px)', backgroundSize:'40px 40px'}} />
            <div className="relative max-w-3xl mx-auto text-center px-4">
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">{t('common.readyToBuild')}</h2>
              <p className="text-white/80 text-lg mb-10">{t('common.letDiscuss')}</p>
              <Link to={'/contact'}
                 className="inline-block bg-white text-theme-color px-10 py-4 rounded-full text-lg font-black hover:shadow-2xl hover:scale-105 transition-all">
                {t('common.startProject')}
              </Link>
            </div>
          </section>
        </div>
      </Suspense>
    </div>
  );
};

export default Home;
